import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Building2, Plus, X } from "lucide-react";
import { RELATIONSHIPS } from "./constants";

const OUR_ORG = ["our org", "our team", "our organization"];
const PARTNER = ["partner", "prime", "sub", "subcontractor", "vendor", "client"];

export default function CompaniesTab() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [filter, setFilter] = useState("all"); // all | our | partners
  const [modal, setModal] = useState(false);

  const load = () => api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const rel = (c) => (c.relationship || "").toLowerCase();
  const visible = companies.filter((c) =>
    filter === "all" ? true : filter === "our" ? OUR_ORG.includes(rel(c)) : PARTNER.includes(rel(c)));

  const setRelationship = async (c, value) => {
    try {
      await api.put(`/records/company/${c.id}`, { fields: { relationship: value } });
      setCompanies((prev) => prev.map((x) => x.id === c.id ? { ...x, relationship: value } : x));
    } catch { toast.error("Update failed"); }
  };

  return (
    <div data-testid="companies-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex gap-1.5">
          {[["all", "All"], ["our", "Our Org"], ["partners", "Partners"]].map(([k, l]) => (
            <button key={k} data-testid={`companies-filter-${k}`} onClick={() => setFilter(k)}
              className={`text-xs px-3 py-1.5 rounded-full border ${filter === k ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"}`}>{l}</button>
          ))}
        </div>
        <button data-testid="add-company-btn" onClick={() => setModal(true)}
          className="flex items-center gap-1.5 bg-zinc-900 text-white font-semibold text-sm px-3 py-2 rounded-md hover:bg-zinc-800">
          <Plus className="h-4 w-4" /> Company</button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="companies-grid">
        {visible.length === 0 && <div className="col-span-full text-xs text-zinc-400 py-10 text-center border border-dashed border-zinc-300 rounded-xl">No companies match this filter.</div>}
        {visible.map((c) => (
          <div key={c.id} data-testid={`company-card-${c.id}`} className="bg-white border border-zinc-200 shadow-sm rounded-xl p-4">
            <button onClick={() => navigate(`/r/company/${c.id}`)} className="text-left w-full">
              <div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-zinc-400" /><span className="text-sm font-semibold text-zinc-900 truncate">{c.name}</span></div>
              <div className="text-[11px] text-zinc-500 mt-1">{c.type || "—"}</div>
              <div className="text-[11px] text-zinc-400">{c.address}</div>
            </button>
            <label className="block mt-3">
              <span className="text-[10px] uppercase tracking-wider text-zinc-400">Relationship</span>
              <select value={c.relationship || ""} onChange={(e) => setRelationship(c, e.target.value)} data-testid={`company-rel-${c.id}`}
                className="w-full mt-1 bg-white border border-zinc-300 rounded-md px-2 py-1.5 text-xs text-zinc-900">
                {RELATIONSHIPS.map((r) => <option key={r} value={r}>{r || "— none —"}</option>)}</select>
            </label>
          </div>
        ))}
      </div>

      {modal && <CompanyModal onClose={() => setModal(false)} onSaved={() => { setModal(false); load(); }} />}
    </div>
  );
}

function CompanyModal({ onClose, onSaved }) {
  const [f, setF] = useState({ name: "", address: "", type: "Government Agency", relationship: "" });
  const submit = async () => {
    if (!f.name) return toast.error("Name required");
    await api.post("/companies", f); toast.success("Company saved"); onSaved();
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="w-full max-w-md bg-white border border-zinc-200 rounded-xl p-6 space-y-3 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="company-modal">
        <div className="flex items-center justify-between"><h2 className="font-display font-bold text-xl text-zinc-900">New company</h2><button onClick={onClose}><X className="h-5 w-5 text-zinc-400 hover:text-zinc-900" /></button></div>
        <F label="Name" v={f.name} on={set("name")} testid="co-name" />
        <F label="Address" v={f.address} on={set("address")} testid="co-address" />
        <F label="Type" v={f.type} on={set("type")} testid="co-type" />
        <label className="block"><span className="text-[11px] uppercase tracking-wider text-zinc-500">Relationship</span>
          <select value={f.relationship} onChange={set("relationship")} data-testid="co-relationship" className="w-full mt-1 bg-white border border-zinc-300 rounded-md px-2 py-2 text-sm text-zinc-900">
            {RELATIONSHIPS.map((r) => <option key={r} value={r}>{r || "— none —"}</option>)}</select></label>
        <button onClick={submit} data-testid="save-company-btn" className="w-full mt-2 bg-zinc-900 text-white font-semibold py-2.5 rounded-md hover:bg-zinc-800">Save</button>
      </div>
    </div>
  );
}
const F = ({ label, v, on, testid }) => (
  <label className="block"><span className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</span>
    <input value={v} onChange={on} data-testid={testid} className="w-full mt-1 bg-white border border-zinc-300 rounded-md px-3 py-2 text-sm text-zinc-900 focus:border-gold-dark outline-none" /></label>
);
